import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Globe,
  Calendar,
  MapPin,
  History,
  AlertTriangle,
  ImageIcon,
  Gauge
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '../../contexts/TeamsContext';
import { BrandApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { BrandInfringement, InfringementEvent } from 'api-client';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';

const InfringementDetails = () => {
  const { id } = useParams();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [infringement, setInfringement] = useState<BrandInfringement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInfringement = useCallback(async () => {
    if (!currentTeam?.slug || !id) return;

    setLoading(true);
    try {
      const brandApi = new BrandApi(getApiConfiguration(token));
      const response = await brandApi.brandInfringementsRetrieve({
        teamSlug: currentTeam.slug,
        id: parseInt(id),
      });
      setInfringement(response);
      setError(null);
    } catch (err) {
      console.error('Error fetching infringement:', err);
      setError('Failed to load infringement details');
    } finally {
      setLoading(false);
    }
  }, [currentTeam?.slug, id, token]);

  useEffect(() => {
    fetchInfringement();
  }, [fetchInfringement]);

  const getSeverityColor = (severity: string | undefined) => {
    switch (severity) {
      case 'critical':
        return 'critical';
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'new':
        return 'default';
      case 'detected':
        return 'info';
      case 'investigating':
        return 'warning';
      case 'reported':
        return 'warning';
      case 'takendown':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !infringement) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">{error || 'Infringement not found'}</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="relative">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">{infringement.threatTitle}</h1>
            <div className="flex items-center space-x-3">
              <Badge variant={getSeverityColor(infringement.severity)}>
                {infringement.severityDisplay}
              </Badge>
              <Badge variant={getStatusColor(infringement.status)}>
                {infringement.statusDisplay}
              </Badge>
              <div className="text-sm text-gray-400">
                Case #{infringement.id}
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="flex flex-col items-center">
              <div className="relative w-16 h-16">
                {/* Circular background */}
                <div className="absolute inset-0 rounded-full bg-gray-800/50 backdrop-blur-sm"></div>
                
                {/* Circular progress */}
                {infringement?.confidenceLevel && typeof infringement.confidenceLevel === 'number' && (
                  <svg className="absolute inset-0 w-full h-full -rotate-90 transition-transform duration-700 ease-out">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      strokeWidth="4"
                      stroke="currentColor"
                      fill="none"
                      className="text-gray-700/30"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      strokeWidth="4"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      className="text-blue-500 drop-shadow-glow transition-all duration-700 ease-out"
                      style={{
                        strokeDasharray: `${2 * Math.PI * 28}`,
                        strokeDashoffset: `${2 * Math.PI * 28 * (1 - (infringement.confidenceLevel || 0) / 100)}`,
                      }}
                    />
                  </svg>
                )}
                
                {/* Center content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {infringement?.confidenceLevel && typeof infringement.confidenceLevel === 'number' ? (
                    <div className="text-center scale-100 hover:scale-110 transition-transform">
                      <div className="text-xl font-bold text-blue-500">{infringement.confidenceLevel}%</div>
                    </div>
                  ) : (
                    <div className="text-center scale-100 hover:scale-110 transition-transform">
                      <Gauge className="h-6 w-6 text-gray-500" />
                      <div className="text-xs text-gray-500 mt-1">Unknown</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-sm font-medium text-gray-400">Confidence</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Key Information */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 space-y-6">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <Globe className="h-4 w-4" />
                  <span>Type</span>
                </div>
                <div className="text-white">{infringement.threatTypeDisplay}</div>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <MapPin className="h-4 w-4" />
                  <span>Source</span>
                </div>
                <div className="text-white">{infringement.source}</div>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-400 mb-2">
                  <Calendar className="h-4 w-4" />
                  <span>Detected</span>
                </div>
                <div className="text-white">
                  {new Date(infringement.dateDetected).toLocaleString()}
                </div>
              </div>
            </div>

            {infringement.description && (
              <div className="border-t border-gray-700/50 pt-6">
                <div className="text-sm text-gray-400 mb-2">Description</div>
                <p className="text-gray-300 leading-relaxed">{infringement.description}</p>
              </div>
            )}
          </div>
          <br></br>
          <AssetNotes objectId={id || ''} objectType="brandinfringement" />

        </div>

        {/* Evidence */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Evidence</h3>
              {infringement.screenshot ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-black/50 ring-1 ring-white/10">
                  <img
                    src={infringement.screenshot}
                    alt="Infringement Screenshot"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ) : (
                <div className="aspect-video rounded-xl overflow-hidden bg-black/50 ring-1 ring-white/10 flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-12 h-12 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-3">
                      <ImageIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-400">No screenshot available</p>
                    <p className="text-xs text-gray-500 mt-1">Evidence is being processed or unavailable</p>
                  </div>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Timeline</h3>
              <div className="space-y-6">
                {infringement.timelineEvents.map((event: InfringementEvent) => (
                  <div key={event.id} className="relative pl-6 pb-6 last:pb-0 border-l border-gray-700/50">
                    <div className="absolute left-0 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-500/20 ring-4 ring-blue-500/10">
                      <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20"></div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-white mb-1">{event.description}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(event.eventDate).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfringementDetails;
